import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Gem } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { PRODUCTS, PRODUCT_CATEGORIES } from "../data/products";
import { waLink } from "../data/site";

export const Shop = () => {
    const [cat, setCat] = useState("All");
    const list = cat === "All" ? PRODUCTS : PRODUCTS.filter((p) => p.cat === cat);

    return (
        <section id="shop" data-testid="shop-section" className="py-28 lg:py-36 px-6">
            <div className="max-w-7xl mx-auto">
                <Reveal className="text-center max-w-2xl mx-auto">
                    <Overline>The Sacred Shop</Overline>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0EBE0]">
                        Transform your space. <span className="italic text-[#C9A962]">Elevate your energy.</span>
                    </h2>
                    <p className="mt-4 text-sm font-light text-[#A19D94]">
                        Handpicked Vastu, Feng Shui & crystal products curated by Maitrii Shah. DM to order.
                    </p>
                </Reveal>

                <Reveal delay={0.1}>
                    <div data-testid="shop-filters" className="mt-12 flex flex-wrap justify-center gap-2">
                        {PRODUCT_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                data-testid={`shop-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                onClick={() => setCat(c)}
                                className={`px-5 py-2.5 rounded-full text-xs uppercase tracking-[0.15em] transition-all ${
                                    cat === c
                                        ? "bg-[#C9A962] text-[#081226] font-semibold"
                                        : "border border-white/10 text-[#A19D94] hover:border-[#C9A962]/40 hover:text-[#C9A962]"
                                }`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                </Reveal>

                <motion.div layout className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
                    <AnimatePresence mode="popLayout">
                        {list.map((p, i) => (
                            <motion.div
                                layout
                                key={p.name}
                                data-testid={`product-${i}`}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                                className="group rounded-2xl border border-white/10 bg-[#0E1B33] overflow-hidden hover:border-[#C9A962]/30 hover:-translate-y-1 hover:shadow-[0_0_40px_rgba(201,169,98,0.1)] transition-all duration-500 flex flex-col"
                            >
                                <div className="relative aspect-square overflow-hidden bg-black">
                                    <img
                                        src={`/assets/products/${p.img}.jpg`}
                                        alt={p.name}
                                        loading="lazy"
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-[#081226]/80 backdrop-blur border border-[#C9A962]/30 text-[#C9A962] font-mono-num text-sm">
                                        {p.price}
                                    </span>
                                </div>
                                <div className="p-5 flex flex-col flex-1">
                                    <h3 className="font-display text-base lg:text-lg font-semibold text-[#F0EBE0] leading-snug">{p.name}</h3>
                                    <p className="mt-2 text-xs font-light text-[#A19D94] leading-relaxed flex-1">{p.desc}</p>
                                    <ul className="mt-3 space-y-1.5 hidden sm:block">
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2 text-[11px] text-[#5C5A56]">
                                                <Gem size={11} className="text-[#C9A962]/70 mt-0.5 shrink-0" /> {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <a
                                        data-testid={`product-order-${i}`}
                                        href={waLink(`Hi Maitrii! I'd like to order the ${p.name} (${p.price}).`)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-4 w-full py-2.5 rounded-full border border-[#C9A962]/40 text-[#C9A962] text-[11px] font-semibold uppercase tracking-[0.15em] hover:bg-[#C9A962] hover:text-[#081226] transition-all flex items-center justify-center gap-2"
                                    >
                                        <MessageCircle size={13} /> Order on WhatsApp
                                    </a>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </motion.div>

                <Reveal delay={0.15}>
                    <p className="mt-12 text-center text-xs text-[#5C5A56]">
                        Can't find what you're looking for? <a data-testid="shop-ask-whatsapp" href={waLink("Hi Maitrii! I'm looking for a product recommendation.")} target="_blank" rel="noopener noreferrer" className="text-[#C9A962] hover:underline">Ask Maitrii for a recommendation →</a>
                    </p>
                </Reveal>
            </div>
        </section>
    );
};
